import React from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Shimmer } from "../../../shared/ui";
import { PAGES } from "../../../shared/lib/pageUrlConstants";
import { TransactionItem } from "../types";
import { GeneralDetailsView, OtcDetailsView } from "../components/details";
import { getSingleTransaction } from "../services";

export const TransactionDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Target transaction ID from route parameter
  const targetId = id;

  // Use useQuery to fetch transaction details via centralized service & endpoint registry
  const {
    data: apiResponse,
    isLoading,
  } = useQuery({
    queryKey: ["admin-transaction-details", targetId],
    queryFn: () => getSingleTransaction(targetId!),
    enabled: Boolean(targetId),
  });
  console.log({ apiResponse })
  // Extract transaction item and business balance from API response (primary data source)
  const apiTransaction = React.useMemo(() => {
    const rawTx =
      apiResponse?.data?.transaction ??
      apiResponse?.data ??
      apiResponse?.transaction ??
      (apiResponse?._id ? apiResponse : undefined);

    if (!rawTx?._id) return undefined;

    const businessBalance = apiResponse?.data?.businessBalance;
    const walletBalanceNum = businessBalance?.wallets?.[0]?.balance;

    // Normalize any nested object fields on business (e.g., name: { value: "...", status: "..." })
    const rawBusiness = rawTx.business;
    const normalizedBusiness = rawBusiness
      ? {
        ...rawBusiness,
        name:
          typeof rawBusiness.name === "object" && rawBusiness.name !== null
            ? (rawBusiness.name as any).value || (rawBusiness.name as any).name || ""
            : rawBusiness.name,
        registrationNumber:
          typeof rawBusiness.registrationNumber === "object" && rawBusiness.registrationNumber !== null
            ? (rawBusiness.registrationNumber as any).value || ""
            : rawBusiness.registrationNumber,
        wallet: {
          balance: walletBalanceNum !== undefined ? walletBalanceNum : rawBusiness.wallet?.balance,
          currency: businessBalance?.wallets?.[0]?.currency || businessBalance?.currency,
        },
      }
      : undefined;

    return {
      ...rawTx,
      balance: walletBalanceNum !== undefined ? walletBalanceNum : rawTx.balance,
      business: normalizedBusiness,
    } as TransactionItem;
  }, [apiResponse]);

  // Route state passed from table row navigation used as initial/fallback state
  const stateTransaction = location.state?._id
    ? (location.state as TransactionItem)
    : undefined;

  // Primary source is apiTransaction from the live API response, with fallback to location.state
  const transaction = apiTransaction || stateTransaction;

  // Determine if this transaction view should render OTC or General details
  const tabParam = searchParams.get("tab") || location.state?.tab;
  const isOtc =
    tabParam === "OTC" ||
    location.state?.tab === "OTC" ||
    location.state?.mainTab === "OTC" ||
    location.state?.TransactionType === "OTC" ||
    location.state?.type === "OTC" ||
    Boolean(transaction?.otc) ||
    Boolean(apiTransaction?.otc) ||
    transaction?.source === "OTC" ||
    transaction?.TransactionType === "OTC" ||
    transaction?.type === "OTC" ||
    Boolean(id?.startsWith("otc-")) ||
    Boolean(id?.startsWith("req-"));

  // Sibling navigation tracking (matching KYB details pattern)
  const locationState = location.state as
    | (TransactionItem & { transactionIds?: string[]; tab?: string })
    | null;
  const transactionIds = locationState?.transactionIds ?? [];
  const currentTransactionIndex = targetId
    ? transactionIds.findIndex((tId) => tId === targetId)
    : -1;
  const previousTransactionId =
    currentTransactionIndex > 0 ? transactionIds[currentTransactionIndex - 1] : null;
  const nextTransactionId =
    currentTransactionIndex >= 0 && currentTransactionIndex < transactionIds.length - 1
      ? transactionIds[currentTransactionIndex + 1]
      : null;

  const handleNavigateSibling = (delta: number) => {
    const nextId = delta < 0 ? previousTransactionId : nextTransactionId;
    if (!nextId) return;

    const nextTab = tabParam || (isOtc ? "OTC" : "General");
    navigate(`${PAGES.transactionsV2}/${nextId}?tab=${nextTab}`, {
      state: {
        tab: nextTab,
        transactionIds,
      },
    });
  };

  if (isLoading && !transaction) {
    return (
      <section className="min-h-full bg-[#F3F4F8] p-6 space-y-4">
        <Shimmer className="h-10 w-48 rounded-lg" />
        <Shimmer className="h-44 w-full rounded-2xl" />
        <Shimmer className="h-72 w-full rounded-2xl" />
      </section>
    );
  }

  if (!transaction) {
    return (
      <section className="min-h-full bg-[#F3F4F8] md:p-6">
        <div className="rounded-[16px] bg-white p-8 text-center">
          <h2 className="text-[18px] font-semibold text-[#101828]">
            Transaction not found
          </h2>
          <p className="mt-1 text-[14px] text-[#667085]">
            No transaction found matching ID &ldquo;{targetId}&rdquo;.
          </p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-4 h-10 rounded-[8px] bg-[#020618] px-5 text-[13px] font-medium text-white transition-colors hover:bg-[#0f172a]"
          >
            Go back
          </button>
        </div>
      </section>
    );
  }

  if (isOtc) {
    return (
      <OtcDetailsView
        transaction={transaction}
        id={targetId}
        canGoPrevious={Boolean(previousTransactionId)}
        canGoNext={Boolean(nextTransactionId)}
        onNavigateSibling={handleNavigateSibling}
      />
    );
  }

  return (
    <GeneralDetailsView
      transaction={transaction}
      id={targetId}
      canGoPrevious={Boolean(previousTransactionId)}
      canGoNext={Boolean(nextTransactionId)}
      onNavigateSibling={handleNavigateSibling}
    />
  );
};

export default TransactionDetailsPage;
